import React, { useState } from "react";
import ProfileInput from "./ProfileInput";
import { Button } from "@material-tailwind/react";
import { useReduxStore } from "../hooks/useReduxStore";
import { useForm } from "react-hook-form";
import { EditBasicInfoAction } from "../store/Actions/action";
import Schemas from "../utilities/validations";
import UserAvatar from "./UserAvatar";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useEmailVerification } from "../hooks/useEmailVerification";
import ProfileSuccessMessage from "./ProfileSuccessMessage";
import OTPVerificationModal from "./OTPVerificationModal";
import { updateProfile, sendOTP, verifyOTP } from "../services/profileService";
import { toast } from "react-toastify";
import { updateAuth } from "../store/Reducers/authReducer";
// import EmailVerificationModal from "../pages/Login/EmailVerificationModal";
export default function BasicInfoComponent({setShowEmailVerificationModal}) {
  const { getState, dispatch } = useReduxStore();
  const [isEditingName, setIsEditingName] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const { isEmailVerified, isVerifying, sendVerificationEmail } = useEmailVerification();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: Schemas.editBasicInfo,
    defaultValues: {
      name: "",
    }
  });

  const onSave = async (data = {}) => {
    setIsUpdatingProfile(true);
    try {
      const response = await updateProfile(data);
      if (response.ok && response.data.success) {
        // Update user data in Redux store
        dispatch(updateAuth({ 
          userData: response.data.data 
        }));
        setIsEditingName(false);
        setShowSuccessMessage(true);
        // toast.success("Profile updated successfully!");
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        toast.error(response.data?.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const onVerifyEmail = async () => {
    setShowEmailVerificationModal(true)
    // setIsSendingOTP(true);
    // try {
    //   const response = await sendOTP(email);
    //   if (response.ok && response.data.success) {
    //     setShowOTPModal(true);
    //     toast.success("OTP sent to your email!");
    //   } else {
    //     toast.error(response.data?.message || "Failed to send OTP");
    //   }
    // } catch (error) {
    //   console.error("Send OTP error:", error);
    //   toast.error("Failed to send OTP. Please try again.");
    // } finally {
    //   setIsSendingOTP(false);
    // }
  };

  const handleOTPVerify = async (otp) => {
    setIsVerifyingOTP(true);
    try {
      const response = await verifyOTP(email, otp);
      if (response.ok && response.data.message) {
        setShowOTPModal(false);
        toast.success("Email verified successfully!");
        // Update email verification status in Redux
        dispatch(EditBasicInfoAction({ 
          userData: { ...userData, isVerified: true } 
        }));
      } else {
        toast.error(response.data?.message || "Invalid OTP");
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      toast.error("Failed to verify OTP. Please try again.");
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const { userData } = getState("AUTH");
  const { name, email, avatar } = userData || {};

  return (
    <>
      <ProfileSuccessMessage show={showSuccessMessage} />
      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        email={email}
        onVerify={handleOTPVerify}
        isLoading={isVerifyingOTP}
      />
      <div className="space-y-6">
      {/* User Avatar */}
      <div className="flex flex-col items-center mb-8">
        <div className="relative">
          <UserAvatar name={name} size="w-32 h-32" className="mb-4 shadow-lg" />
          {/* <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-primaryBlue rounded-full flex items-center justify-center">
            <CheckCircleIcon className="h-5 w-5 text-white" />
          </div> */}
        </div>
        <h2 className="text-2xl font-semibold text-white">{name}</h2>
        <p className="text-sm text-gray-300 mt-1">Profile Information</p>
      </div>

      {/* Name Field */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-white">Name</label>
          {!isEditingName && (
            <button
              onClick={() => setIsEditingName(true)}
              className="border border-white text-white px-3 py-1 rounded hover:bg-primaryBlue text-xs font-medium transition-colors"
            >
              Edit
            </button>
          )}
        </div>
        
        {isEditingName ? (
          <div className="space-y-3">
            <ProfileInput
              label=""
              defaultValue={name}
              placeholder="Enter your name"
              type="text"
              id="name"
              name="name"
              errors={errors}
              register={register}
            />
            <div className="flex gap-2">
              <button
                onClick={handleSubmit(onSave)}
                className="bg-primaryBlue text-white px-4 py-2 rounded hover:bg-primaryBlue/90 text-xs font-medium transition-colors disabled:opacity-50"
                disabled={isUpdatingProfile}
              >
                {isUpdatingProfile ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => setIsEditingName(false)}
                className="border border-white text-white px-4 py-2 rounded hover:bg-gray-600 text-xs font-medium transition-colors"
                disabled={isUpdatingProfile}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-[#384355] rounded-md border border-borderGray hover:border-primaryBlue transition-colors">
            <span className="text-white font-medium">{name}</span>
          </div>
        )}
      </div>

      {/* Email Field */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-white">Email</label>
          <div className="flex items-center gap-2">
            {isEmailVerified ? (
              <div className="flex items-center gap-1 text-green-400">
                <CheckCircleIcon className="h-4 w-4" />
                <span className="text-xs font-medium">Verified</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-orange-400">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span className="text-xs font-medium">Unverified</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1 p-3 bg-[#384355] rounded-md border border-borderGray">
            <span className="text-white font-medium">{email}</span>
          </div>
          {!isEmailVerified && (
            <button
              onClick={onVerifyEmail}
              className="bg-primaryBlue text-white px-4 py-2 rounded hover:bg-primaryBlue/90 text-xs font-medium whitespace-nowrap transition-colors disabled:opacity-50"
              disabled={isSendingOTP}
            >
              {isSendingOTP ? "Sending..." : "Verify"}
            </button>
          )}
        </div>
      </div>
     

      </div>
    </>
  );
}
